import EmblaCarousel, { EmblaCarouselType } from 'embla-carousel'

type Options = {
  mobileOnly?: boolean
  imageSelector?: string
  align?: 'start' | 'center' | 'end'
  loop?: boolean
  startIndex?: number
  containScroll?: boolean
}

export default ({
  mobileOnly = false,
  imageSelector = '',
  align = 'start',
  loop = false,
  startIndex = 0,
  containScroll = true,
}: Options = {}) => ({
  _embla: null as EmblaCarouselType | null,
  _mediaQuery: null as MediaQueryList | null,
  _mqHandler: null as ((e: MediaQueryListEvent) => void) | null,
  _resizeObserver: null as ResizeObserver | null,
  isActive: false,
  canScrollPrev: false,
  canScrollNext: false,
  buttonStyle: {} as Record<string, string>,

  init() {
    if (mobileOnly) {
      this._mediaQuery = window.matchMedia('(min-width: 1024px)')

      this._mqHandler = (e: MediaQueryListEvent) => {
        if (e.matches) {
          this._destroyEmbla()
        } else {
          this._initEmbla()
        }
      }

      this._mediaQuery.addEventListener('change', this._mqHandler)

      if (!this._mediaQuery.matches) {
        this._initEmbla()
      }
    } else {
      this._initEmbla()
    }
  },

  destroy() {
    if (this._mediaQuery && this._mqHandler) {
      this._mediaQuery.removeEventListener('change', this._mqHandler)
    }

    this._destroyEmbla()
  },

  _initEmbla() {
    const viewport = (this as any).$refs.viewport as HTMLElement

    this._embla = EmblaCarousel(viewport, {
      loop,
      align,
      startIndex,
      containScroll: containScroll ? 'trimSnaps' : false,
    })
    this._embla.on('init', () => this._updateButtons())
    this._embla.on('reInit', () => this._updateButtons())
    this._embla.on('select', () => this._updateButtons())
    this._embla.on('select', () => this._dispatchSelect())
    this._embla.on('init', () => this._updateActiveSlide())
    this._embla.on('reInit', () => this._updateActiveSlide())
    this._embla.on('select', () => this._updateActiveSlide())
    this.isActive = true
    this._updateButtons()
    this._updateActiveSlide()

    if (imageSelector) {
      const imageEl = viewport.querySelector(imageSelector) as HTMLElement | null

      if (imageEl) {
        this._resizeObserver = new ResizeObserver(() => this._updateButtonPosition(viewport))
        this._resizeObserver.observe(imageEl)
        this._updateButtonPosition(viewport)
      }
    }
  },

  _destroyEmbla() {
    this._resizeObserver?.disconnect()
    this._resizeObserver = null
    this.buttonStyle = {}
    this._embla?.destroy()
    this._embla = null
    this.isActive = false
    this.canScrollPrev = false
    this.canScrollNext = false
  },

  _updateButtons() {
    this.canScrollPrev = this._embla?.canScrollPrev() ?? false
    this.canScrollNext = this._embla?.canScrollNext() ?? false
  },

  _updateButtonPosition(viewport: HTMLElement) {
    const imageEl = viewport.querySelector(imageSelector) as HTMLElement | null

    if (!imageEl) return

    const h = imageEl.getBoundingClientRect().height

    this.buttonStyle = { top: `${h / 2}px` }
  },

  prev() {
    this._embla?.scrollPrev()
  },

  next() {
    this._embla?.scrollNext()
  },

  scrollTo(index: number, jump = false) {
    this._embla?.scrollTo(index, jump)
  },

  reInit() {
    this._embla?.reInit()
  },

  _dispatchSelect() {
    const index = this._embla?.selectedScrollSnap() ?? 0
    this.$dispatch('carousel:select', { index })
  },

  _updateActiveSlide() {
    const selected = this._embla?.selectedScrollSnap() ?? 0

    this._embla?.slideNodes().forEach((slide, i) => {
      slide.classList.toggle('is-active', i === selected)
    })
  },
})
